import { FC } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useEvent } from '~/hooks/useEvent';
import { useImageViewer } from '~/components/contexts/ImageViewerContext';
import { ZoomableMedia } from '~/components/molecules/ImageZoomModal';
import { isVideo } from '~/utils/media';
import { HorizontalScroll } from '~/components/molecules/HorizontalScroll';

export interface Media {
  id: number | string;
  url: string;
  type?: 'image' | 'video';
}

interface MediaDisplayProps {
  media: Media[];
  title?: string;
  className?: string;
  contextId?: string;
  isDetailPage?: boolean;
  isPriority?: boolean;
}

/** 
 * For now images are unoptimized because we don't have a CDN
 * Without a CDN with prooper image service, nextjs can not handle the image sizes it creates memory issues
 * TODO: Add a CDN for images
 */

export const MediaDisplay: FC<MediaDisplayProps> = ({
  media,
  title = '',
  className = '',
  contextId,
  isDetailPage = false,
  isPriority = false,
}) => {
  const { t } = useTranslation();
  const { sendEvent } = useEvent();
  const { openImageViewer } = useImageViewer();

  if (!media || media.length === 0) return null;

  const isSingleItem = media.length === 1;

  // Calculate accurate sizes attribute based on display context
  const getSizes = (isSingle: boolean) => {
    if (isSingle) {
      // For single item display, adjust based on max-width and viewport.
      // Assumes roughly 90% width on smaller screens, capped at 1000px.
      return '(max-width: 1023px) 90vw, 1000px';
    } else {
      // For horizontal scroll items with a fixed width.
      return '280px';
    }
  };

  const handleMediaClick = (index: number) => {
    if (!isVideo(media[index].url)) {
      sendEvent('media_item_click', {
        index,
        mediaId: media[index].id,
        mediaType: 'image',
        context: contextId || 'unknown',
      });

      // Filter out videos and create proper zoomable media objects
      const zoomableMedia: ZoomableMedia[] = media
        .filter(item => !isVideo(item.url))
        .map(item => ({
          id: item.id,
          url: item.url,
          type: 'image'
        }));

      // Find the correct index in the filtered array
      const clickedItem = media[index];
      const filteredIndex = zoomableMedia.findIndex(img => img.id === clickedItem.id);
      openImageViewer(zoomableMedia, filteredIndex >= 0 ? filteredIndex : 0, title);
    }
  };

  // Render media item - video
  const renderVideo = (item: Media, index: number) => (
    <div
      key={item.id}
      className="aspect-square overflow-hidden rounded-lg cursor-pointer"
      onClick={() => handleMediaClick(index)}
    >
      <video
        src={`${item.url}#t=0.01`}
        controls
        className="w-full h-full object-cover rounded-lg"
        aria-label={t('media.video', { index: index + 1 }) || ''}
      />
    </div>
  );

  // Render media item - image
  const renderImage = (item: Media, index: number) => (
    <div
      key={item.id}
      className={`aspect-square overflow-hidden rounded-lg cursor-pointer ${isSingleItem ? 'max-w-[1000px] mx-auto' : ''}`}
      onClick={() => handleMediaClick(index)}
    >
      {isSingleItem ? (
        <Image
          src={item.url}
          alt={`${title} ${t('media.image', { index: index + 1 }) || ''}`}
          width={1000}
          height={1000}
          className="object-cover w-full h-full"
          priority={isDetailPage || isPriority}
          sizes={getSizes(true)}
        />
      ) : (
        <div className="relative w-full h-full">
          <Image
            src={item.url}
            alt={`${title} ${t('media.image', { index: index + 1 }) || ''}`}
            fill
            className="object-cover"
            priority={isDetailPage || isPriority}
            sizes={getSizes(false)}
          />
        </div>
      )}
    </div>
  );

  // Single item display
  if (isSingleItem) {
    const item = media[0];
    return (
      <div className={className}>
        {isVideo(item.url) ? renderVideo(item, 0) : renderImage(item, 0)}
      </div>
    );
  }

  // Multiple items - always use horizontal scroll
  return (
    <div className={className}>
      <HorizontalScroll
        itemWidth="280px"
        className="w-full"
      >
        {media.map((item, index) => (
          isVideo(item.url) ? renderVideo(item, index) : renderImage(item, index)
        ))}
      </HorizontalScroll>
    </div>
  );
};

export default MediaDisplay; 